import { type ReactNode } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Typography, Chip, Stack, Divider, Box,
  CircularProgress, Alert, Link,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getTenant, getTenantAdmin, TENANT_STATUS_META, type TenantRow } from '../../services/tenantService';
import { usePlatformServices } from '../../hooks/useServiceCatalog';
import {
  countEnabledTenantServiceModules,
  filterTenantServiceModules,
  TENANT_ORPHAN_MODULE_CODES,
  TENANT_NON_SERVICE_MODULE_CODES,
} from '../../constants/tenantModules';
import { resolveLanguageOptions } from '../../constants/platformLanguages';
import { resolveAssetUrl } from '../../utils/assetUrl';

interface Props {
  open: boolean;
  tenant: TenantRow | null;
  onClose: () => void;
  onEdit: () => void;
  onModules: () => void;
  onSuspend: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
}

function Field({ label, value, fullWidth }: { label: string; value?: string | number | null; fullWidth?: boolean }) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
        {value != null && value !== '' ? value : '—'}
      </Typography>
    </Grid>
  );
}

function formatDate(value?: string | null, withTime = false) {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return withTime ? d.toLocaleString() : d.toLocaleDateString();
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.08 }}>{title}</Typography>
      <Grid container spacing={2} sx={{ mt: 0.25 }}>{children}</Grid>
    </Box>
  );
}

export default function TenantViewDialog({
  open, tenant: listTenant, onClose, onEdit, onModules, onSuspend, onActivate, onDeactivate, onDelete,
}: Props) {
  const tenantId = listTenant?._id;
  const { data: platformServices = [] } = usePlatformServices();
  const serviceModules = filterTenantServiceModules(platformServices);
  const nameByCode = new Map(serviceModules.map((m) => [m.code, m.name]));

  const { data: freshTenant, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenant(tenantId!),
    enabled: open && !!tenantId,
    staleTime: 30_000,
  });

  const { data: adminContact } = useQuery({
    queryKey: ['tenant-admin', tenantId],
    queryFn: () => getTenantAdmin(tenantId!),
    enabled: open && !!tenantId,
    staleTime: 30_000,
  });

  const tenant = freshTenant || listTenant;
  if (!open || !listTenant) return null;

  if (!tenant) return null;

  const status = (tenant.status || (tenant.isActive ? 'active' : 'deactivated')) as keyof typeof TENANT_STATUS_META;
  const meta = TENANT_STATUS_META[status] || TENANT_STATUS_META.active;
  const excluded = new Set<string>([...TENANT_NON_SERVICE_MODULE_CODES, ...TENANT_ORPHAN_MODULE_CODES]);
  const enabledModules = (tenant.modules || []).filter((m) => m.enabled && !excluded.has(m.key));
  const disabledModules = (tenant.modules || []).filter((m) => !m.enabled && !excluded.has(m.key));
  const enabledCount = countEnabledTenantServiceModules(
    tenant.modules,
    serviceModules.map((m) => m.code),
  );
  const languageLabels = resolveLanguageOptions(tenant.languages || []).map((l) => l.name).join(', ')
    || (tenant.languages || []).join(', ');
  const defaultLanguageLabel = resolveLanguageOptions([tenant.defaultLanguage || ''])[0]?.name
    || tenant.defaultLanguage;
  const adminEmail = adminContact?.email || listTenant.adminEmail || '';
  const adminPhone = adminContact?.phone || listTenant.adminPhone || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="h6" fontWeight={700}>{tenant.companyName}</Typography>
          <Chip label={meta.label} color={meta.color} size="small" />
          {!tenant.isActive && status !== 'deactivated' && (
            <Chip label="Inactive flag" size="small" variant="outlined" />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">/{tenant.slug}</Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 280 }}>
        {isLoading && !freshTenant && (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }} spacing={1.5}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">Loading tenant details…</Typography>
          </Stack>
        )}

        {isError && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" onClick={() => void refetch()}>Retry</Button>}
          >
            {(error as { response?: { data?: { message?: string } } })?.response?.data?.message
              || 'Could not refresh full tenant details. Showing list data.'}
          </Alert>
        )}

        {(!isLoading || freshTenant) && (
          <>
            <Section title="Company">
              <Field label="Company name" value={tenant.companyName} />
              <Field label="Admin email" value={adminEmail} />
              <Field label="Admin phone" value={adminPhone} />
              <Field label="Slug" value={tenant.slug} />
              <Field label="Tenant ID" value={tenant._id} fullWidth />
              <Field label="Status" value={meta.label} />
              <Field label="Active" value={tenant.isActive ? 'Yes' : 'No'} />
              <Field label="Subscription plan" value={tenant.subscriptionPlan} />
              <Field label="Plan expiry" value={formatDate(tenant.subscriptionExpiry)} />
            </Section>

            <Divider sx={{ my: 1 }} />

            <Section title="Locale & domain">
              <Field label="Country" value={tenant.country} />
              <Field label="Currency" value={tenant.currency} />
              <Field label="Timezone" value={tenant.timezone} />
              <Field label="Default language" value={defaultLanguageLabel} />
              <Field label="Languages" value={languageLabels || undefined} fullWidth />
              <Field label="Custom domain" value={tenant.domain} />
              <Field label="Subdomain" value={tenant.subdomain} />
            </Section>

            <Divider sx={{ my: 1 }} />

            <Section title="Branding">
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Primary color</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: tenant.branding?.primaryColor || '#173A5E',
                      border: 1,
                      borderColor: 'divider',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {tenant.branding?.primaryColor || '#173A5E'}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Secondary color</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: tenant.branding?.secondaryColor || '#5C6B7A',
                      border: 1,
                      borderColor: 'divider',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {tenant.branding?.secondaryColor || '#5C6B7A'}
                  </Typography>
                </Stack>
              </Grid>
              <Field label="Font family" value={tenant.branding?.fontFamily} fullWidth />
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Logo</Typography>
                {tenant.branding?.logo ? (
                  <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 220,
                        height: 96,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        p: 1,
                        bgcolor: 'grey.50',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={resolveAssetUrl(tenant.branding.logo)}
                        alt={`${tenant.companyName || 'Tenant'} logo`}
                        onError={(e) => {
                          const el = e.currentTarget as HTMLImageElement;
                          el.style.display = 'none';
                          const fallback = el.parentElement?.querySelector('[data-logo-fallback]');
                          if (fallback) (fallback as HTMLElement).style.display = 'block';
                        }}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          objectPosition: 'center',
                        }}
                      />
                      <Typography
                        data-logo-fallback
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'none' }}
                      >
                        Logo unavailable
                      </Typography>
                    </Box>
                    <Link
                      href={resolveAssetUrl(tenant.branding.logo)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                    >
                      Open logo URL
                    </Link>
                  </Stack>
                ) : (
                  <Typography variant="body2" fontWeight={600}>—</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Favicon</Typography>
                {tenant.branding?.favicon ? (
                  <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        bgcolor: 'grey.50',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={resolveAssetUrl(tenant.branding.favicon)}
                        alt="Tenant favicon"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                    <Link
                      href={resolveAssetUrl(tenant.branding.favicon)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                    >
                      Open favicon URL
                    </Link>
                  </Stack>
                ) : (
                  <Typography variant="body2" fontWeight={600}>—</Typography>
                )}
              </Grid>
            </Section>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="overline" color="text.secondary">
                Services ({enabledCount} enabled)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Enabled
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
                {enabledModules.map((m) => (
                  <Chip key={m.key} label={nameByCode.get(m.key) || m.key} size="small" color="primary" variant="outlined" />
                ))}
                {!enabledModules.length && (
                  <Typography variant="body2" color="text.secondary">No modules enabled</Typography>
                )}
              </Stack>
              {!!disabledModules.length && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Not enabled
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {disabledModules.map((m) => (
                      <Chip key={m.key} label={nameByCode.get(m.key) || m.key} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Section title="Audit">
              <Field label="Created" value={formatDate(tenant.createdAt, true)} />
              <Field label="Updated" value={formatDate(tenant.updatedAt, true)} />
            </Section>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={onModules} disabled={status === 'deleted'}>Modules</Button>
        <Button onClick={onEdit} disabled={status === 'deleted'}>Edit</Button>
        {status === 'active' && <Button color="warning" onClick={onSuspend}>Suspend</Button>}
        {status === 'suspended' && <Button color="success" onClick={onActivate}>Activate</Button>}
        {(status === 'active' || status === 'suspended') && (
          <Button color="inherit" onClick={onDeactivate}>Deactivate</Button>
        )}
        {status !== 'deleted' && <Button color="error" onClick={onDelete}>Delete</Button>}
      </DialogActions>
    </Dialog>
  );
}
